import React, { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { HelpBubble } from "../components/HelpBubble";
import {
  ShieldCheck,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  FileText,
  LogIn,
  UserX,
  KeyRound,
  Download,
  Trash2,
  UserPlus,
  AlertTriangle,
  PenLine,
  CheckCircle,
  XCircle,
  Send,
  RotateCcw,
  Flag,
  Target,
  ArrowRight,
  Clock,
  ScrollText,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const actionIcons = {
  login_success: <LogIn size={14} className="text-emerald-600" />,
  login_failed: <AlertTriangle size={14} className="text-red-600" />,
  user_registered: <UserPlus size={14} className="text-blue-600" />,
  password_changed: <KeyRound size={14} className="text-amber-600" />,
  data_export: <Download size={14} className="text-violet-600" />,
  account_deletion: <Trash2 size={14} className="text-red-600" />,
  gdpr_consent: <ShieldCheck size={14} className="text-emerald-600" />,
  contract_created: <PenLine size={14} className="text-blue-600" />,
  contract_updated: <PenLine size={14} className="text-amber-600" />,
  contract_deleted: <Trash2 size={14} className="text-red-600" />,
  contract_archived: <FileText size={14} className="text-slate-500" />,
  workflow_submitted: <Send size={14} className="text-blue-600" />,
  workflow_created: <Send size={14} className="text-blue-600" />,
  review_approved: <CheckCircle size={14} className="text-emerald-600" />,
  review_rejected: <XCircle size={14} className="text-red-600" />,
  revision_requested: <RotateCcw size={14} className="text-amber-600" />,
  revision_resubmitted: <Send size={14} className="text-blue-600" />,
  approval_step_approved: <CheckCircle size={14} className="text-emerald-600" />,
  approval_approved: <CheckCircle size={14} className="text-emerald-600" />,
  approval_rejected: <XCircle size={14} className="text-red-600" />,
  contract_approved: <CheckCircle size={14} className="text-emerald-700" />,
  contract_rejected: <XCircle size={14} className="text-red-700" />,
  signature_requested: <PenLine size={14} className="text-violet-600" />,
  contract_executed: <CheckCircle size={14} className="text-emerald-700" />,
  obligation_updated: <Target size={14} className="text-amber-600" />,
  milestone_updated: <Flag size={14} className="text-blue-600" />,
  role_changed: <UserX size={14} className="text-amber-600" />,
};

const actionColors = {
  login_success: "bg-emerald-100 text-emerald-700",
  login_failed: "bg-red-100 text-red-700",
  user_registered: "bg-blue-100 text-blue-700",
  password_changed: "bg-amber-100 text-amber-700",
  data_export: "bg-violet-100 text-violet-700",
  account_deletion: "bg-red-100 text-red-700",
  gdpr_consent: "bg-emerald-100 text-emerald-700",
  contract_created: "bg-blue-100 text-blue-700",
  contract_updated: "bg-amber-100 text-amber-700",
  contract_deleted: "bg-red-100 text-red-700",
  contract_archived: "bg-slate-100 text-slate-600",
  workflow_submitted: "bg-blue-100 text-blue-700",
  workflow_created: "bg-blue-100 text-blue-700",
  review_approved: "bg-emerald-100 text-emerald-700",
  review_rejected: "bg-red-100 text-red-700",
  revision_requested: "bg-amber-100 text-amber-700",
  revision_resubmitted: "bg-blue-100 text-blue-700",
  approval_step_approved: "bg-emerald-100 text-emerald-700",
  approval_approved: "bg-emerald-100 text-emerald-700",
  approval_rejected: "bg-red-100 text-red-700",
  contract_approved: "bg-emerald-100 text-emerald-700",
  contract_rejected: "bg-red-100 text-red-700",
  signature_requested: "bg-violet-100 text-violet-700",
  contract_executed: "bg-emerald-100 text-emerald-700",
  obligation_updated: "bg-amber-100 text-amber-700",
  milestone_updated: "bg-blue-100 text-blue-700",
  role_changed: "bg-amber-100 text-amber-700",
};

// Phase grouping for contract lifecycle
const phaseOf = (action) => {
  if (["contract_created", "contract_updated"].includes(action)) return "Drafting";
  if (["workflow_submitted", "workflow_created", "review_approved", "review_rejected", "revision_requested", "revision_resubmitted"].includes(action)) return "Review";
  if (["approval_step_approved", "approval_approved", "approval_rejected", "contract_approved", "contract_rejected"].includes(action)) return "Approval";
  if (["signature_requested", "contract_executed"].includes(action)) return "Execution";
  if (["obligation_updated", "milestone_updated"].includes(action)) return "Management";
  if (["contract_deleted", "contract_archived"].includes(action)) return "Lifecycle";
  return "Other";
};

const phaseColors = {
  Drafting: "border-l-blue-500",
  Review: "border-l-amber-500",
  Approval: "border-l-violet-500",
  Execution: "border-l-emerald-500",
  Management: "border-l-cyan-500",
  Lifecycle: "border-l-slate-400",
  Other: "border-l-slate-300",
};

function AuditLogEntry({ log, showContract }) {
  const phase = phaseOf(log.action);
  return (
    <div className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors border-l-4 ${phaseColors[phase]}`}>
      <div className="w-8 h-8 rounded-sm bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
        {actionIcons[log.action] || <FileText size={14} className="text-slate-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`text-xs rounded-sm ${actionColors[log.action] || "bg-slate-100 text-slate-600"}`}>
            {log.action?.replace(/_/g, " ")}
          </Badge>
          <Badge variant="outline" className="text-xs rounded-sm">{phase}</Badge>
          <span className="text-xs text-slate-500">{log.user_email}</span>
          {showContract && log.contract_title && (
            <span className="text-xs text-cyan-600">{log.contract_title}</span>
          )}
        </div>
        <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">{log.details}</p>
      </div>
      <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
        {log.timestamp ? new Date(log.timestamp).toLocaleString() : ""}
      </span>
    </div>
  );
}

function ContractAuditTrail({ contracts }) {
  const [contractId, setContractId] = useState("");
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTrail = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/audit/contract/${id}`);
      setTrail(res.data);
    } catch { toast.error("Failed to load audit trail"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (contractId) fetchTrail(contractId);
  }, [contractId, fetchTrail]);

  // Group trail by phase
  const grouped = {};
  if (trail?.trail) {
    trail.trail.forEach((log) => {
      const phase = phaseOf(log.action);
      if (!grouped[phase]) grouped[phase] = [];
      grouped[phase].push(log);
    });
  }

  const phases = ["Drafting", "Review", "Approval", "Execution", "Management", "Lifecycle", "Other"];

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <Select value={contractId} onValueChange={setContractId}>
          <SelectTrigger className="w-full rounded-sm" data-testid="trail-contract-select">
            <SelectValue placeholder="Select a contract to view its audit trail..." />
          </SelectTrigger>
          <SelectContent>
            {contracts.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-slate-400" size={32} />
        </div>
      )}

      {trail && !loading && (
        <>
          {/* Contract header */}
          <Card className="border-slate-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>{trail.contract_title}</p>
                  <p className="text-xs text-slate-500 mt-1">Status: <Badge className="text-xs rounded-sm ml-1">{trail.contract_status}</Badge></p>
                </div>
                <Badge className="bg-slate-100 text-slate-600 rounded-sm">{trail.total_events} events</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Phase progression */}
          <div className="flex items-center gap-1 flex-wrap">
            {phases.filter(p => grouped[p]?.length > 0).map((phase, i, arr) => (
              <React.Fragment key={phase}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border-l-4 bg-white ${phaseColors[phase]}`}>
                  <span className="text-xs font-medium">{phase}</span>
                  <Badge variant="outline" className="text-xs h-5 px-1.5">{grouped[phase].length}</Badge>
                </div>
                {i < arr.length - 1 && <ArrowRight size={14} className="text-slate-300 mx-1" />}
              </React.Fragment>
            ))}
          </div>

          {/* Timeline */}
          <Card>
            <CardContent className="p-0 divide-y">
              {trail.trail.length === 0 ? (
                <div className="text-center py-12 text-slate-400">No audit events recorded for this contract</div>
              ) : trail.trail.map((log, i) => (
                <AuditLogEntry key={log.id || log.timestamp || i} log={log} showContract={false} />
              ))}
            </CardContent>
          </Card>
        </>
      )}

      {!trail && !loading && (
        <Card>
          <CardContent className="py-20 text-center text-slate-400">
            <ScrollText size={40} className="mx-auto mb-3 opacity-50" />
            <p>Select a contract to view its full audit trail</p>
            <p className="text-xs mt-1">Covers drafting, review, approval, execution, and management</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function AuditLogPage() {
  const [searchParams] = useSearchParams();
  const initialContractTab = searchParams.get("contract") ? "contract" : "all";

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [actionTypes, setActionTypes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const limit = 50;

  useEffect(() => {
    fetchActionTypes();
    fetchContracts();
  }, []);
  useEffect(() => { fetchLogs(); }, [page, actionFilter]);

  const fetchActionTypes = async () => {
    try {
      const res = await apiClient.get("/audit/actions");
      setActionTypes(res.data);
    } catch { /* ok */ }
  };

  const fetchContracts = async () => {
    try {
      const res = await apiClient.get("/contracts");
      setContracts(res.data.items || res.data);
    } catch { /* ok */ }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (actionFilter && actionFilter !== "all") params.append("action", actionFilter);
      if (emailFilter) params.append("user_email", emailFilter);
      const res = await apiClient.get(`/audit/logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load audit logs");
    } finally { setLoading(false); }
  };

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Audit Log <HelpBubble tip="Immutable record of every action taken in the system — logins, contract changes, approvals, and more. Captured automatically by the global audit middleware." />
          </h1>
          <p className="text-slate-500 mt-1">End-to-end audit trail for statutory compliance and security</p>
        </div>
      </div>

      <Tabs defaultValue={initialContractTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="all" className="gap-2" data-testid="tab-all-logs">
            <ShieldCheck size={16} /> All Activity ({total})
          </TabsTrigger>
          <TabsTrigger value="contract" className="gap-2" data-testid="tab-contract-trail">
            <ScrollText size={16} /> Contract Audit Trail
          </TabsTrigger>
        </TabsList>

        {/* All Activity Tab */}
        <TabsContent value="all">
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-10 rounded-sm"
                  placeholder="Filter by email..."
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="audit-email-filter"
                />
              </div>
              <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="audit-action-filter">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map((a) => (
                    <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={handleSearch}>Search</Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm gap-1.5"
                data-testid="audit-export-csv"
                onClick={() => {
                  const params = new URLSearchParams({ format: "csv" });
                  if (actionFilter && actionFilter !== "all") params.append("action", actionFilter);
                  if (emailFilter) params.append("user_email", emailFilter);
                  window.open(`${apiClient.defaults.baseURL}/audit/logs/export?${params.toString()}`, "_blank");
                }}
              >
                <Download size={14} /> CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm gap-1.5"
                data-testid="audit-export-pdf"
                onClick={() => {
                  const params = new URLSearchParams({ format: "pdf" });
                  if (actionFilter && actionFilter !== "all") params.append("action", actionFilter);
                  if (emailFilter) params.append("user_email", emailFilter);
                  window.open(`${apiClient.defaults.baseURL}/audit/logs/export?${params.toString()}`, "_blank");
                }}
              >
                <Download size={14} /> PDF
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="animate-spin text-slate-400" size={32} />
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-20 text-slate-400">
                    <ShieldCheck size={40} className="mx-auto mb-3 opacity-50" />
                    <p>No audit logs found</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {logs.map((log, i) => (
                      <AuditLogEntry key={log.id || log.timestamp || i} log={log} showContract={true} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    <ChevronLeft size={14} />
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                    <ChevronRight size={14} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Contract Audit Trail Tab */}
        <TabsContent value="contract">
          <ContractAuditTrail contracts={contracts} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
